import { Avatar, Overline, UIText1 } from '@cian/ui-kit';
import { Image } from '@cian/ui-kit/image';

import { type FC, type JSX, useCallback, useMemo } from 'react';

import { ButtonLink } from 'shared/components/Button';
import { Layout } from 'shared/components/Layout';
import { Marquee } from 'shared/components/Marquee';
import { Transport } from 'shared/components/Transport';
import { WrapElementsSpacer } from 'shared/components/WrapElementsSpacer';
import { type RemotenessTypes } from 'shared/types/RemotenessTypes';
import { type TransportTypes } from 'shared/types/TransportTypes';
import { extractUndefined } from 'shared/utils/extractUndefined';
import { splitStringToMaxCharsInRow } from 'shared/utils/splitStringToMaxCharsInRow';

import { BackgroundVideo } from '../BackgroundVideo';
import { Badges, type BadgesTypes } from '../Badges';

import styles from './Slide.css';

type TransportType = {
  id: number;
  name: string;
  iconColor?: string | null;
  remoteness?: string | null;
  remotenessType?: RemotenessTypes | null;
  iconType?: TransportTypes | null;
};

type Video = {
  url: string;
  posterUrl: string;
};

type SlideProps = {
  imageUrl: string;
  title: string;
  video: Video | null;
  disclaimer: string | null;
  label: string | null;
  publicUrl: string;
  badges: BadgesTypes[];
  transports: TransportType[];
  hasCommercialProperty: boolean;
  logoUrl: string | null;
  newbuildingId: number;
  testId: string;
  active: boolean;
  onSelectApartmentClick: (newbuildingId: number) => void;
};

export const Slide: FC<SlideProps> = props => {
  const {
    imageUrl,
    title,
    disclaimer,
    label,
    publicUrl,
    badges,
    transports,
    hasCommercialProperty,
    logoUrl,
    newbuildingId,
    testId,
    video,
    active,
    onSelectApartmentClick,
  } = props;

  const handleSelectApartmentClick = useCallback((): void => {
    onSelectApartmentClick(newbuildingId);
  }, [newbuildingId, onSelectApartmentClick]);

  const splitTitleViews = useMemo<Array<string | JSX.Element>>(() => {
    const splitTitle = splitStringToMaxCharsInRow(title, 24);
    const lastSplitTitleIndex = splitTitle.length - 1;

    const result = Array.of<string | JSX.Element>();

    for (let index = 0; index < lastSplitTitleIndex; index += 1) {
      result.push(splitTitle[index], <br key={`br_${index}`} />);
    }

    result.push(
      <WrapElementsSpacer horizontalSpace={16} key={lastSplitTitleIndex + 1} verticalSpace={0}>
        {splitTitle[lastSplitTitleIndex]}
        <Badges badges={badges} />
      </WrapElementsSpacer>,
    );

    return result;
  }, [badges, title]);

  return (
    <div className={styles['slide']} data-testid={testId}>
      <div className={styles['slide__media-wrapper']}>
        {video ? (
          <BackgroundVideo active={active} poster={video.posterUrl} url={video.url} />
        ) : (
          <Image alt={title} data-testid="image" height="100%" objectFit="cover" src={imageUrl} width="100%" />
        )}
      </div>

      <div className={styles['information-container']}>
        <Layout>
          <div className={styles['information__wrapper']}>
            <div className={styles['content']}>
              <div className={styles['main-content']}>
                <div className={styles['avatar-wrapper']}>
                  <Avatar data-testid="avatar" size={56} src={logoUrl} type="builder" />
                </div>

                {!!transports.length && (
                  <div className={styles['transports-wrapper']} data-testid="transports-wrapper">
                    {transports.map(transport => (
                      <Transport
                        iconColor={extractUndefined(transport.iconColor)}
                        iconType={extractUndefined(transport.iconType)}
                        key={transport.id}
                        name={transport.name}
                        remoteness={extractUndefined(transport.remoteness)}
                        remotenessType={extractUndefined(transport.remotenessType)}
                      />
                    ))}
                  </div>
                )}

                <div className={styles['title']} data-testid="title">
                  {splitTitleViews}
                </div>
                <UIText1 color="text-inverted-default" data-testid="label">
                  {label}
                </UIText1>
              </div>
              <div className={styles['button-wrapper']}>
                <div className={styles['button-wrapper__item']}>
                  <ButtonLink href={publicUrl} testId="select-apartment-button" onClick={handleSelectApartmentClick}>
                    Выбрать квартиру
                  </ButtonLink>
                </div>
                {hasCommercialProperty && (
                  <Overline color="text-inverted-default" data-testid="commercial-property-info">
                    <sup>*</sup>в проекте есть в продаже коммерческая недвижимость
                  </Overline>
                )}
              </div>
            </div>
            {disclaimer && (
              <div className={styles['disclaimer']} data-testid="disclaimer">
                <Marquee text={disclaimer} />
              </div>
            )}
          </div>
        </Layout>
      </div>
    </div>
  );
};
